/**
 * The interrupted-session recovery model shared by main, preload and the control
 * renderer (ticket 13, FR-47).
 *
 * A take that never reached an MP4 — because the app was killed, the machine
 * lost power, or the remux itself failed — leaves its chunks on disk. FR-45 is
 * what makes those chunks worth having; this is what turns them back into a
 * file. Everything here is the part that needs neither a filesystem nor ffmpeg,
 * so it can be unit-tested in Node and stated once.
 */

/** Where one recoverable take has got to. */
export type RecoveryStatus =
  | "offered"
  | "recovering"
  | "recovered"
  | "failed";

/** One interrupted take, as offered to the user. */
export interface RecoveryOffer {
  /** The session id, which is also the directory its chunks are in. */
  id: string;
  /** When the take was shot, ISO-8601 — not when it was found, so the user
   * recognises which take this is. */
  startedAt: string;
  /** The document it was recorded against, where the session recorded one. */
  title?: string;
  /** How much footage is sitting there, so "is this worth recovering?" can be
   * answered without opening anything. */
  bytes: number;
  /** Where the chunks are. Shown with the offer, because a user who declines
   * needs to know what they are declining and where it will still be. */
  chunkPath: string;
  status: RecoveryStatus;
  /** The MP4 it was recovered into, once it has been. */
  output?: string;
  /** Why it could not be recovered, if it could not. */
  message?: string;
}

/** The recovery state the control window is a view onto, broadcast on change. */
export interface RecoveryState {
  offers: RecoveryOffer[];
}

/** The state before the launch scan has run, and after everything found by it
 * has been dealt with. */
export const EMPTY_RECOVERY_STATE: RecoveryState = { offers: [] };

/**
 * When a recovered take was shot, from its sidecar's timestamp — the fallback
 * standing in for a sidecar that has been truncated or hand-edited into
 * something `Date` cannot parse.
 *
 * It matters because the timestamp names the output file (FR-48): without the
 * guard an unparseable one produces `NaN-NaN-NaN NaNNaNNaN.mp4`, and a user
 * whose take was recovered under that name would reasonably conclude something
 * had gone wrong with the footage too.
 */
export function sessionStartedAt(startedAt: string, fallback: number): Date {
  return parseStartedAt(startedAt) ?? new Date(fallback);
}

/** The sidecar's timestamp as a date, or null when it is not one. */
function parseStartedAt(startedAt: string): Date | null {
  const parsed = new Date(startedAt);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A byte count at the scale the number is worth reading at. Only ever shown
 * alongside a recovery offer, where the question it answers is "is there a take
 * in there or a false start?" — so one decimal place from MB up is plenty and a
 * thousands-separated byte count would be worse.
 *
 * 1024-based with `KB`/`MB` labels, matching File Explorer rather than SI: the
 * user's next move after reading this is to look at the file, and a size that
 * disagrees with the one Windows shows them would read as a different file.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1_024) return `${Math.round(bytes)} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1_024;
  let unit = 0;
  while (value >= 1_024 && unit < units.length - 1) {
    value /= 1_024;
    unit++;
  }
  // Under 10 of a unit, the first decimal is the difference between "1 MB" and
  // "1.8 MB"; above it, it is noise.
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/**
 * How a recoverable take is described in the offer: when it was shot, and what
 * it was shot against. Local time, because the user is recovering a take they
 * remember making this afternoon.
 *
 * An unparseable timestamp falls back to the session id rather than to now: the
 * id is itself a readable timestamp, and telling the user a take from yesterday
 * was shot a moment ago would make it harder to identify, not easier.
 */
export function describeOffer(offer: RecoveryOffer): string {
  const parsed = parseStartedAt(offer.startedAt);
  const when = parsed ? parsed.toLocaleString() : offer.id;
  return offer.title ? `${when} — ${offer.title}` : when;
}
